// Request examples for the endpoint API tab (ui-spec §5.13) and the inference playground. Every value comes from
// the endpoint, the model or the playground's form, the secret stays `$NODUS_API_KEY`, and each example is a complete
// request the OpenAI-compatible routes accept.
import { cliCommand, type SnippetVariant } from "@nodus/ui";
import { decisionSnippets, decisionExample } from "./decisions";
import type { Scope } from "./model";

const PROMPT = "Say hello in one sentence.";

export type ChatMessage = { role: "system" | "user"; content: string };

/** The chat completions body the playground sends and its snippets show; unset sampling fields are omitted. */
export interface ChatRequest {
  model: string;
  messages: ChatMessage[];
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
}

const sampling = (r: ChatRequest) =>
  [
    ["temperature", r.temperature],
    ["max_tokens", r.max_tokens],
  ].filter((f): f is [string, number] => f[1] !== undefined);

/** curl, Python and TypeScript for one chat completion against an OpenAI-compatible base URL. */
export function chatSnippets(req: ChatRequest, baseURL: string): SnippetVariant[] {
  const url = JSON.stringify(baseURL);
  const q = JSON.stringify;
  const json = JSON.stringify({ model: req.model, messages: req.messages, ...Object.fromEntries(sampling(req)), ...(req.stream ? { stream: true } : {}) }, null, 2);
  const py = [
    `    model=${q(req.model)},`,
    "    messages=[",
    ...req.messages.map((m) => `        {"role": ${q(m.role)}, "content": ${q(m.content)}},`),
    "    ],",
    ...sampling(req).map(([k, v]) => `    ${k}=${v},`),
    ...(req.stream ? ["    stream=True,"] : []),
  ];
  const ts = [
    `  model: ${q(req.model)},`,
    "  messages: [",
    ...req.messages.map((m) => `    { role: ${q(m.role)}, content: ${q(m.content)} },`),
    "  ],",
    ...sampling(req).map(([k, v]) => `  ${k}: ${v},`),
    ...(req.stream ? ["  stream: true,"] : []),
  ];
  return [
    {
      id: "curl",
      label: "curl",
      code: [
        `curl ${req.stream ? "-N " : ""}${baseURL}/chat/completions \\`,
        '  -H "Authorization: Bearer $NODUS_API_KEY" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '${json.replaceAll("'", "'\\''").replaceAll("\n", "\n  ")}'`,
      ].join("\n"),
    },
    {
      id: "python",
      label: "Python",
      code: [
        "import os",
        "from openai import OpenAI",
        "",
        `client = OpenAI(base_url=${url}, api_key=os.environ["NODUS_API_KEY"])`,
        `${req.stream ? "stream" : "reply"} = client.chat.completions.create(`,
        ...py,
        ")",
        ...(req.stream
          ? ["for chunk in stream:", "    if chunk.choices:", '        print(chunk.choices[0].delta.content or "", end="", flush=True)']
          : ["print(reply.choices[0].message.content)"]),
      ].join("\n"),
    },
    {
      id: "typescript",
      label: "TypeScript",
      code: [
        'import OpenAI from "openai";',
        "",
        `const client = new OpenAI({ baseURL: ${url}, apiKey: process.env.NODUS_API_KEY });`,
        `const ${req.stream ? "stream" : "reply"} = await client.chat.completions.create({`,
        ...ts,
        "});",
        ...(req.stream
          ? ["for await (const chunk of stream) {", '  process.stdout.write(chunk.choices[0]?.delta.content ?? "");', "}"]
          : ["console.log(reply.choices[0]?.message.content);"]),
      ].join("\n"),
    },
  ];
}

/** A transcription model's example: the playground sends chat completions only, so audio stays in the terminal. */
export function transcriptionSnippet(model: string, baseURL: string): SnippetVariant {
  return {
    id: "curl",
    label: "curl",
    code: [`curl ${baseURL}/audio/transcriptions \\`, '  -H "Authorization: Bearer $NODUS_API_KEY" \\', `  -F model=${model} \\`, "  -F file=@audio.mp3"].join("\n"),
  };
}

/** Embedding examples use input text and read vectors; they have no chat sampling or output-token fields. */
export function embeddingSnippets(model: string, baseURL: string): SnippetVariant[] {
  const m = JSON.stringify(model);
  const input = JSON.stringify(["first document", "second document"]);
  return [
    {
      id: "curl",
      label: "curl",
      code: [
        `curl ${baseURL}/embeddings \\`,
        '  -H "Authorization: Bearer $NODUS_API_KEY" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '${JSON.stringify({ model, input: ["first document", "second document"] }).replaceAll("'", "'\\''")}'`,
      ].join("\n"),
    },
    {
      id: "python",
      label: "Python",
      code: [
        "import os",
        "from openai import OpenAI",
        "",
        `client = OpenAI(base_url=${JSON.stringify(baseURL)}, api_key=os.environ["NODUS_API_KEY"])`,
        `vectors = client.embeddings.create(model=${m}, input=${input})`,
        "print(vectors.data[0].embedding)",
      ].join("\n"),
    },
    {
      id: "typescript",
      label: "TypeScript",
      code: [
        'import OpenAI from "openai";',
        "",
        `const client = new OpenAI({ baseURL: ${JSON.stringify(baseURL)}, apiKey: process.env.NODUS_API_KEY });`,
        `const vectors = await client.embeddings.create({ model: ${m}, input: ${input} });`,
        "console.log(vectors.data[0].embedding);",
      ].join("\n"),
    },
  ];
}

export function endpointSnippets({
  name,
  model,
  baseURL,
  scope,
  omitScope,
  decisions = false,
  embeddings = false,
}: {
  name: string;
  model: string;
  baseURL: string;
  scope: Scope;
  omitScope: boolean;
  decisions?: boolean;
  embeddings?: boolean;
}): SnippetVariant[] {
  if (decisions) return decisionSnippets(decisionExample(model), baseURL);
  if (embeddings) return embeddingSnippets(model, baseURL);
  const m = JSON.stringify(model);
  const messages = `[{"role": "user", "content": ${JSON.stringify(PROMPT)}}]`;
  const [curl, python, typescript] = chatSnippets({ model, messages: [{ role: "user", content: PROMPT }] }, baseURL);
  return [
    python!,
    typescript!,
    curl!,
    {
      id: "nodus",
      label: "Nodus SDK",
      code: [
        "import nodus",
        "",
        `client = nodus.llm.openai(endpoint=${JSON.stringify(name)}, project=${JSON.stringify(scope.project)})`,
        "reply = client.chat.completions.create(",
        `    model=${m},`,
        `    messages=${messages},`,
        ")",
        "print(reply.choices[0].message.content)",
      ].join("\n"),
    },
    { id: "cli", label: "CLI", code: cliCommand(["inference", "chat", "--endpoint", name, PROMPT], scope, { omitScope }) },
  ];
}
